import { forwardRef, useImperativeHandle, useState } from "react";

import "./stylesUserBalanceModal.css";

import {
  CloseOutlined,
  CommentOutlined,
  DollarOutlined,
} from "@ant-design/icons";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { message, Select } from "antd";
import CustomInput from "../../../../CommonComponents/CustomInput/CustomInput";
import { BRAND_ICONS_URLS } from "../../../../../utils/Constants";
import { calculateUpdatedBalances } from "../../../../../utils/walletUtils";
import { getApiErrorMessage } from "../../../../../utils/ErrorUtils";
import { useSelector } from "react-redux";

const ACTION_OPTIONS = [
  { value: "add", label: AppStrings.ADD },
  { value: "subtract", label: AppStrings.SUBTRACT },
];

const CASINO_CURRENCY_OPTIONS = [
  { label: AppStrings.CASINO_CASH, value: "casino_cash" },
];

const PIKKEM_CURRENCY_OPTIONS = [
  { label: AppStrings.PIKKEM, value: "p_cash" },
  { label: AppStrings.PIKKEM_REDEEMABLE, value: "redeemable_p_cash" },
];

const PREDICTION_CURRENCY_OPTIONS = [
  { label: AppStrings.PREDICTION_CASH, value: "prediction_cash" },
  {
    label: AppStrings.PREDICTION_REDEEMABLE,
    value: "redeemable_prediction_cash",
  },
];

const UserBalanceModal = forwardRef(({ handleCancel, data, refreshUserData, isSweepSTakesClient, hasCasinoProvider, isPikkemClient, isPredictionsClient }, ref) => {
  const { appConfig } = useSelector((state) => state.config);
  const hasSecondaryCurrency =
    appConfig?.app_currency_config?.application_type === "sweepstakes" &&
    !!appConfig?.app_currency_config?.secondary_currency;

  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const BASE_CURRENCY_OPTIONS = [
    { label: AppStrings.PRIMARY_CURRENCY, value: "rebet_cash" },
    ...(hasSecondaryCurrency
      ? [{ label: AppStrings.SECONDARY_CURRENCY, value: "rebet_coin" }]
      : []),
    { label: AppStrings.REDEEMABLE_CASH, value: "redeemable_cash" },
  ];

  const CURRENCY_OPTIONS = [
    ...BASE_CURRENCY_OPTIONS,
    ...(hasCasinoProvider ? CASINO_CURRENCY_OPTIONS : []),
    ...(isPikkemClient ? PIKKEM_CURRENCY_OPTIONS : []),
    ...(isPredictionsClient ? PREDICTION_CURRENCY_OPTIONS : []),
  ];

  const [isApiRunning, setIsApiRunning] = useState(false);
  const [formData, setFormData] = useState({
    action: undefined,
    amount: "",
    currency: undefined,
    reason: "",
  });

  const clearFormData = () => {
    setFormData({
      user_id: "",
      action: undefined,
      amount: "",
      currency: undefined,
      reason: "",
    });
    setIsApiRunning(false);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsApiRunning(true);
    const operation = formData.action === "add" ? "credit" : "debit";
    const params = {
      user_id: data.user_id,
      operation,
      amount: formData.amount,
      money_type: formData.currency,
      reason: formData.reason,
    };

    
    try {
      // Validate and calculate updated balances BEFORE making the API request
      const valuesToRefresh = calculateUpdatedBalances(data, operation, formData.currency, formData.amount);

      await PrivateAdminPanelInstance?.patch(API_ROUTES?.EDIT_USER_WALLET?.replace("{user_id}", data?.user_id), params);

      refreshUserData(valuesToRefresh);

      message.success(AppStrings.USER_BALANCE_UPDATED_SUCCESSFULLY);
      // Clear form so support can perform another edit without closing the modal
      setFormData({
        user_id: "",
        action: undefined,
        amount: "",
        currency: undefined,
        reason: "",
      });
    } catch (error) {
      console.error("Error updating user balance:", error);
      // Prefer the backend validation detail (e.g. 422 "Input should be greater
      // than 0"); fall back to local errors thrown by calculateUpdatedBalances.
      message.error(
        getApiErrorMessage(error, error?.message || AppStrings.ERROR_PROCESSING_REQUEST)
      );
    } finally {
      setIsApiRunning(false);
      // Modal stays open after Save; only close via the X button (handleCancel)
    }
  };

  const handleUserBalanceNoteLabel = () => {
    const lastChar = data.username.charAt(data.username.length - 1);
    if (lastChar !== "s") {
      return AppStrings.TO_USER_WALLET.replace("user", data.username + "'s");
    }
    return AppStrings.TO_USER_WALLET.replace("user", data.username + "'");
  };

  useImperativeHandle(ref, () => ({
    clearFormData,
  }));

  return (
    <div className="user-balance-modal-container">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title"> {AppStrings.USER_BALANCE}</h2>
        </div>
        <CloseOutlined onClick={handleCancel} className="close-icon" />
      </div>

      <div className="user-balance-body">
        <div className="rebet-cash-container">
          <div className="rebet-cash-container-inner-row">
            <div className="balance rebet-cash">
              <div className="balance-icon-container">
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.primaryCurrencySymbol
                  }
                  alt="cash icon"
                  style={{ width: "20px", height: "20px" }}
                />
                <span>
                  {Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(data.rebet_cash || data.primary_currency_balance) || 0}
                </span>
              </div>
              <p className="balance-text">{AppStrings.PRIMARY_CURRENCY}</p>
            </div>
            {isSweepSTakesClient && (
              <div className="balance rebet-coins">
                <div className="balance-icon-container">
                  <img
                    src={
                      process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                      BRAND_ICONS_URLS.secondaryCurrencySymbol
                    }
                    alt="coin icon"
                    style={{ width: "20px", height: "20px" }}
                  />
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(data.rebet_coin || data.secondary_currency_balance) || 0}
                  </span>
                </div>
                <p className="balance-text">{AppStrings.SECONDARY_CURRENCY}</p>
              </div>
            )}
            <div className="balance rebet-cash redeemable-balance">
              <div className="balance-icon-container">
                <img
                  src={
                    process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                    BRAND_ICONS_URLS.primaryCurrencySymbol
                  }
                  alt="cash icon"
                  style={{ width: "20px", height: "20px" }}
                />
                <span>
                  {Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format((data.rebet_cash || data.primary_currency_balance) - (data.not_wagered_rebet_cash || data.primary_not_wagered_currency_balance)) || 0}
                </span>
              </div>
              <p className="balance-text">{AppStrings.REDEEMABLE_CASH}</p>
            </div>
            {hasCasinoProvider && (
              <div className="balance casino-cash">
                <div className="balance-icon-container">
                  <img
                    src={
                      process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
                      BRAND_ICONS_URLS.primaryCurrencySymbol
                    }
                    alt="casino cash icon"
                    style={{ width: "20px", height: "20px" }}
                  />
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(data.casino_cash) || 0}
                  </span>
                </div>
                <p className="balance-text">{AppStrings.CASINO_CASH}</p>
              </div>
            )}
          </div>
          {isPikkemClient && (
            <div
              className="rebet-cash-container-inner-row"
              style={{
                paddingTop: "20px",
                marginTop: "20px",
                justifyContent: "flex-start",
                borderTop: "1px solid var(--grey-750)",
              }}
            >
              <div className="balance pikkem">
                <div className="balance-icon-container">
                  <DollarOutlined style={{ color: "var(--COLOR-PRIMARY)" }} />
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(data?.p_cash) || 0)}
                  </span>
                </div>
                <p className="balance-text">{AppStrings.PIKKEM}</p>
              </div>
              <div className="balance pikkem">
                <div className="balance-icon-container">
                  <DollarOutlined style={{ color: "var(--COLOR-PRIMARY)" }} />
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format((parseFloat(data?.p_cash) || 0) - (parseFloat(data?.not_wagered_p_cash) || 0))}
                  </span>
                </div>
                <p className="balance-text">
                  {AppStrings.PIKKEM_REDEEMABLE}
                </p>
              </div>
            </div>
          )}
          {isPredictionsClient && (
            <div
              className="rebet-cash-container-inner-row"
              style={{
                paddingTop: "20px",
                marginTop: "20px",
                justifyContent: "flex-start",
                borderTop: "1px solid var(--grey-750)",
              }}
            >
              <div className="balance prediction">
                <div className="balance-icon-container">
                  <DollarOutlined style={{ color: "var(--COLOR-PRIMARY)" }} />
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(data?.prediction_cash) || 0)}
                  </span>
                </div>
                <p className="balance-text">{AppStrings.PREDICTION_CASH}</p>
              </div>
              <div className="balance prediction">
                <div className="balance-icon-container">
                  <DollarOutlined style={{ color: "var(--COLOR-PRIMARY)" }} />
                  <span>
                    {Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(
                      (parseFloat(data?.prediction_cash) || 0) -
                        (parseFloat(data?.not_wagered_prediction_cash) || 0),
                    )}
                  </span>
                </div>
                <p className="balance-text">{AppStrings.PREDICTION_REDEEMABLE}</p>
              </div>
            </div>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <p className="user-balance-body-form-group-row-label">
            {AppStrings.I_WANT_TO}
          </p>
          <div className="user-balance-body-form-group-row">
            <div className="form-group" style={{ flex: 1 }}>
              <Select
                id="action"
                showSearch={{ optionFilterProp: "label" }}
                value={formData.action}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, action: value }))
                }
                placeholder={AppStrings.SELECT_ACTION}
                options={ACTION_OPTIONS}
                style={{ width: "100%" }}
              />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <CustomInput
                placeholder={AppStrings.AMOUNT}
                value={formData.amount}
                onChange={(e) => {
                  // Keep only digits and a single decimal point so admins can't
                  // submit "$37.32" / "-681.75" (backend 422s on those).
                  const digitsAndDot = e.target.value.replace(/[^0-9.]/g, "");
                  const [whole, ...decimals] = digitsAndDot.split(".");
                  const amount =
                    decimals.length > 0 ? `${whole}.${decimals.join("")}` : whole;
                  setFormData((prev) => ({ ...prev, amount }));
                }}
                required
                prefix={" "}
                style={{ height: "41px" }}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <Select
                id="currency"
                value={formData.currency}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, currency: value }))
                }
                placeholder={AppStrings.SELECT_CURRENCY}
                options={CURRENCY_OPTIONS}
                style={{ width: "100%" }}
              />
            </div>
          </div>
          <p className="user-balance-body-form-group-row-label">
            {handleUserBalanceNoteLabel()}
          </p>
          <div className="form-group">
            <CustomInput
              placeholder={AppStrings.LEAVE_A_COMMENT}
              value={formData.reason}
              prefix={<CommentOutlined />}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, reason: e.target.value }))
              }
              required
              style={{ height: "41px" }}
            />
          </div>
          <div
            className="user-balance-modal-footer"
            style={{ display: "flex", gap: "10px" }}
          >
            <div
              className="buttons"
              style={{ width: "100%", display: "flex", gap: "10px" }}
            >
              <button type="button" className="cancel-button" onClick={handleCancel} disabled={isApiRunning}>
                  {AppStrings.CLOSE}
              </button>
              <button className="save-button" type="submit" disabled={isApiRunning}>
                {isApiRunning ? (
                  <RebetLoader height={"20px"} width={"20px"} />
                ) : (
                  AppStrings.SAVE
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default UserBalanceModal;
